import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  IconAIVoice,
  IconAnalytics,
  IconChannels,
  IconChatbot,
  IconChevron,
  IconClose,
  IconContentLibrary,
  IconMenu,
  IconOverview,
  IconPatients,
  IconPrograms,
  IconReviewQueue,
  IconSidebarCollapse,
  IconSidebarExpand,
  IconSignOut,
  IconSmartSMS,
  IconVoiceSMS,
} from './icons';
import { reviewAPI } from '../services/api';
import ProfileDialog from './ProfileDialog';
import { Avatar } from './ui';
import { avatarSrc } from '../config/api';
import { cn } from '../utils/cn';
import { useAuth } from '../context/AuthContext';

const navigation = [
  { name: 'Overview',           href: '/',          icon: IconOverview },
  { name: 'Patient Management', href: '/patients',  icon: IconPatients },
  { name: 'Review Queue',       href: '/review-queue', icon: IconReviewQueue },
  { name: 'Content Library',    href: '/reviews',   icon: IconContentLibrary },
  { name: 'Create Programs',    href: '/scheduled', icon: IconPrograms },
];

// Four voice entries -- "Voice SMS", "Send Voice SMS", "Voice Calls",
// "Bulk AI Calls" -- described two channels, each listed twice: once for its
// scheduled campaigns and once for sending immediately. Nobody could tell
// which was which, and the team flagged one as not belonging at all.
//
// There are two voice channels, and the difference is what the patient can
// do: a Voice SMS plays a recording at them, an AI call talks with them.
// So the rail names the two channels; "send now" lives on each channel's own
// page, where the campaigns for it already are. The old routes still work.
const campaignsNavigation = [
  { name: 'Smart SMS',     href: '/smart-sms',   icon: IconSmartSMS },
  { name: 'Voice SMS',     href: '/voice-sms',   icon: IconVoiceSMS },
  { name: 'AI Voice Calls', href: '/voice-calls', icon: IconAIVoice },
  { name: 'AI Chatbot',    href: '/chatbot',     icon: IconChatbot },
];

const otherNavigation = [
  { name: 'Analytics', href: '/analytics', icon: IconAnalytics },
];

/**
 * Remember the rail across reloads.
 *
 * Read lazily rather than in an effect so the sidebar paints at its stored
 * width on the first frame instead of flashing open and snapping shut. Wrapped
 * because Safari in private mode throws on storage access, and a blocked read
 * should just mean "expanded", not a blank app.
 */
const COLLAPSE_KEY = 'sidebar_collapsed';

/**
 * How many items are waiting for a human, for the Review Queue badge.
 *
 * Creating a Smart SMS campaign told you an approval would be needed, and
 * that was the last you heard of it. Nothing is in the queue at creation
 * time -- a scheduled campaign generates its content when it first runs --
 * so the moment that matters is later, and there was no signal for it. You
 * had to think to go and look, which is precisely what nobody did.
 *
 * Polled rather than pushed: a minute of staleness on a count is fine, and
 * the alternative is a socket for one integer. Re-checked on navigation so
 * approving something clears the badge without waiting for the next tick.
 */
function usePendingReviews(userId, pathname) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let live = true;
    const read = () => reviewAPI
      .pendingCount(userId)
      .then((n) => { if (live) setCount(Number(n) || 0); })
      // A failed count must never break the sidebar; show nothing instead.
      .catch(() => { if (live) setCount(0); });
    read();
    const timer = setInterval(read, 60000);
    return () => { live = false; clearInterval(timer); };
  }, [userId, pathname]);

  return count;
}

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
};

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [channelsOpen, setChannelsOpen] = useState(true);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      // Storage blocked — the rail still works, it just won't be remembered.
    }
  }, [collapsed]);

  // Collapsed, the Channels group has no room for its disclosure chevron or
  // its labels, so the rail shows one flat icon list instead and everything
  // stays a single click away. Both lists are rendered and gated by CSS rather
  // than swapped in state: `collapsed` only takes effect from lg up, so a
  // state-level swap would show the flat list *and* the grouped one on mobile,
  // duplicating every channel link in the drawer.
  const flatNav = [...navigation, ...campaignsNavigation, ...otherNavigation];
  const location = useLocation();
  const { user, logout } = useAuth();
  const pendingReviews = usePendingReviews(user?.id ?? null, location.pathname);

  // full_name is what the API returns. `user_metadata` was the Supabase shape
  // and never existed on this payload, so the name always fell through to the
  // email prefix — the sidebar read "demo" for a user called Dr. Ama Boateng.
  const displayName =
    user?.full_name ||
    user?.email?.split('@')[0] ||
    'Admin';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-white border-r border-gray-200 transform transition-all duration-300 ease-in-out lg:translate-x-0',
          // The drawer is always full width on mobile — it slides over the page
          // rather than squeezing it, so there is nothing to collapse there.
          'w-64',
          collapsed ? 'lg:w-[72px]' : 'lg:w-64',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div
          className={cn(
            'flex h-16 flex-shrink-0 items-center border-b border-gray-200',
            collapsed ? 'lg:justify-center lg:px-2' : 'justify-between px-6'
          )}
        >
          {/* Collapsed, the wordmark has nowhere to go, so the rail shows the
              mark on its own. It is a real asset cropped from the lockup rather
              than a CSS crop of it: the mark is 46% of the lockup's width, so a
              square object-cover box sliced the right edge off it. */}
          <img
            src="/care-arena-logo.png"
            alt="Care Arena"
            className={cn('h-9', collapsed ? 'lg:hidden' : 'w-auto')}
          />
          {collapsed && (
            <img
              src="/care-arena-mark.png"
              alt="Care Arena"
              className="hidden h-8 w-auto lg:block"
            />
          )}
          <button
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
            className="text-gray-500 hover:text-gray-700 lg:hidden"
          >
            <IconClose size={22} />
          </button>
        </div>

        {/* Navigation */}
        <nav
          className={cn(
            'flex-1 space-y-1 overflow-y-auto py-6',
            collapsed ? 'lg:px-2 px-4' : 'px-4'
          )}
        >
          {/* Collapsed rail — desktop only, one flat icon list. */}
          {collapsed && (
            <div className="hidden space-y-1 lg:block">
              {flatNav.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    // With no label on screen, the name becomes the tooltip.
                    title={item.name}
                    aria-label={item.name}
                    className={cn(
                      'flex items-center justify-center rounded-lg py-2.5 transition-colors',
                      isActive
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    )}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon size={18} className="flex-shrink-0" />
                  </Link>
                );
              })}
            </div>
          )}

          {/* Expanded rail, and the mobile drawer at any state. */}
          <div className={cn('space-y-1', collapsed && 'lg:hidden')}>
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors',
                    isActive
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon size={18} className="flex-shrink-0" />
                  <span>{item.name}</span>
                  {/* The standing signal that work is waiting. Without it the
                      only way to learn a campaign needed approving was to
                      remember to go and check. */}
                  {item.href === '/review-queue' && pendingReviews > 0 && (
                    <span
                      className={cn(
                        'ml-auto min-w-[1.375rem] rounded-full px-1.5 py-0.5',
                        'text-center text-xs font-semibold tabular-nums',
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'bg-amber-100 text-amber-900',
                      )}
                      aria-label={`${pendingReviews} waiting for approval`}
                    >
                      {pendingReviews > 99 ? '99+' : pendingReviews}
                    </span>
                  )}
                </Link>
              );
            })}

            <div className="pt-2">
              <button
                onClick={() => setChannelsOpen(!channelsOpen)}
                aria-expanded={channelsOpen}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <IconChannels size={18} />
                  <span className="font-medium">Channels</span>
                </div>
                <IconChevron
                  size={16}
                  className={cn('transition-transform', channelsOpen ? 'rotate-180' : '')}
                />
              </button>
              {channelsOpen && (
                <div className="mt-1 space-y-1 pl-4">
                  {campaignsNavigation.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={cn(
                          'flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm',
                          isActive
                            ? 'bg-primary-50 text-primary-700 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        )}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Icon size={16} />
                        <span>{item.name}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="pt-2 space-y-1">
              {otherNavigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      'flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors',
                      isActive
                        ? 'bg-primary-50 text-primary-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    )}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon size={18} />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>

        {/* Footer – user info + logout */}
        <div
          className={cn(
            'flex-shrink-0 border-t border-gray-200 p-4',
            collapsed && 'lg:px-2'
          )}
        >
          <button
            type="button"
            onClick={() => setProfileOpen(true)}
            title={collapsed ? `${displayName} — edit profile` : 'Edit profile'}
            className={cn(
              'mb-3 flex w-full items-center space-x-3 rounded-lg p-1 text-left transition-colors hover:bg-gray-100',
              collapsed && 'lg:mb-2 lg:justify-center lg:space-x-0'
            )}
          >
            <Avatar src={avatarSrc(user?.avatar_url)} name={displayName} size={32} />
            <div className={cn('min-w-0', collapsed && 'lg:hidden')}>
              <p className="truncate text-sm font-medium text-gray-900">{displayName}</p>
              <p className="truncate text-xs text-gray-500">
                {user?.title || user?.email || ''}
              </p>
            </div>
          </button>
          <button
            onClick={logout}
            title={collapsed ? 'Sign out' : undefined}
            className={cn(
              'flex w-full items-center rounded-lg px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900',
              collapsed ? 'space-x-2 lg:justify-center lg:space-x-0 lg:px-0' : 'space-x-2'
            )}
          >
            <IconSignOut size={16} className="flex-shrink-0" />
            <span className={collapsed ? 'lg:hidden' : ''}>Sign out</span>
          </button>
        </div>
      </div>

      {/* Main content */}
      <div
        className={cn(
          'transition-[padding] duration-300 ease-in-out',
          collapsed ? 'lg:pl-[72px]' : 'lg:pl-64'
        )}
      >
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 h-16 flex items-center justify-between px-6">
          <div className="flex items-center">
            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
              className="lg:hidden text-gray-500 hover:text-gray-700 mr-4"
            >
              <IconMenu size={22} />
            </button>
            {/* Lives in the top bar rather than in the rail: collapsed, the rail
                is 72px of centred icons with nowhere to put a control, and the
                toggle would move as the rail resized. */}
            <button
              onClick={() => setCollapsed((v) => !v)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-pressed={collapsed}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="mr-3 hidden rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900 lg:block"
            >
              {collapsed ? <IconSidebarExpand size={18} /> : <IconSidebarCollapse size={18} />}
            </button>
            <h1 className="text-lg font-semibold text-gray-900">
              {navigation.find((item) => item.href === location.pathname)?.name ||
               campaignsNavigation.find((item) => item.href === location.pathname)?.name ||
               otherNavigation.find((item) => item.href === location.pathname)?.name ||
               (location.pathname.startsWith('/review/') ? 'Review Detail' : 'Dashboard')}
            </h1>
          </div>
          <button
            type="button"
            onClick={() => setProfileOpen(true)}
            aria-label="Edit profile"
            className="hidden items-center space-x-3 rounded-full py-1 pl-1 pr-3 transition-colors hover:bg-gray-100 lg:flex"
          >
            <Avatar src={avatarSrc(user?.avatar_url)} name={displayName} size={32} />
            <span className="text-left">
              <span className="block text-sm font-medium leading-tight text-gray-700">
                {displayName}
              </span>
              {user?.title && (
                <span className="block text-xs leading-tight text-gray-400">{user.title}</span>
              )}
            </span>
          </button>
        </div>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>

      <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
    </div>
  );
};

export default Layout;
